import type { AutocompleteRenderOptionState, FilterOptionsState } from "@mui/material";
import { Autocomplete, createFilterOptions, TextField } from "@mui/material";
import { HTMLAttributes, useRef } from "react";
import { Stateful } from "../../../hooks/useStateful";
import { AlertIcon, CheckIcon, SearchIcon } from "../../Icons/Icons";
import ErrorText from "../../Text/ErrorText";
import Text, { TextVariant } from "../../Text/Text";

type ComboboxProps = {
  options: ComboboxOption[];
  onChange: (selected: ComboboxOption | null) => void;
  label: string;
  defaultValue?: ComboboxOption;
  noOptionsText?: string;
  open?: Stateful<boolean>;
  renderOption?: (
    props: HTMLAttributes<HTMLLIElement>,
    option: ComboboxOption,
    state: AutocompleteRenderOptionState,
  ) => JSX.Element;
  optionsLimit?: number;
  disabled?: boolean;
  errorHelpText?: string;
  showIcon?: boolean;
  helpText?: string;
  disableInput?: boolean;
  readOnlyInput?: boolean;
  inputMode?: "number" | "text";
  onDarkBg?: boolean;
  ariadescribedby?: string;
  placeholder?: string;
  showId?: boolean;
};

export default function Combobox({
  options,
  onChange,
  label,
  defaultValue,
  noOptionsText,
  renderOption,
  open,
  optionsLimit = 42,
  disabled,
  errorHelpText,
  helpText,
  showIcon,
  disableInput = false,
  readOnlyInput = false,
  inputMode = "text",
  onDarkBg,
  ariadescribedby,
  placeholder,
  showId,
}: ComboboxProps): JSX.Element {
  const defaultFilterOptions = createFilterOptions<ComboboxOption>();
  const anchorRef = useRef<HTMLDivElement>(null);
  const filterOptions = (options: ComboboxOption[], state: FilterOptionsState<ComboboxOption>) => {
    return defaultFilterOptions(options, state).slice(0, optionsLimit);
  };

  const providedRenderOption = renderOption;
  const optionsWithEmptyValue = options.concat([{ label: "", id: "", data: {} }]);
  const optionsPopperOffset = showIcon ? -18 : 0;
  return (
    <div className="w-full" ref={anchorRef}>
      <label
        className={`text-xl mb-px ${onDarkBg ? "text-white" : "text-ldir-brown-900"} ${
          errorHelpText ? "font-bold text-ldir-red-900" : ""
        } `}
        htmlFor={label}
      >
        {label}
      </label>
      <div
        className={`flex grow items-center bg-white border-2 py-0.5 rounded  ${
          errorHelpText
            ? "border-ldir-red-900 border-[3px]"
            : onDarkBg
              ? "border-ldir-blue-900"
              : "border-ldir-brown-900"
        }`}
      >
        {showIcon && <SearchIcon className="ml-4 text-ldir-blue-900" />}
        <Autocomplete
          id={label}
          popupIcon={
            <div className="px-1.5 py-2">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="30"
                height="15"
                viewBox="0 0 30 15"
                fill="none"
                role="graphics-symbol"
              >
                <path d="M30 0L15 15L1.31134e-06 -2.62268e-06L30 0Z" fill="#006178" />
              </svg>
            </div>
          }
          aria-describedby={helpText || errorHelpText ? label + "helpText" : undefined}
          key={defaultValue?.id}
          getOptionKey={(option) => option.id}
          getOptionLabel={(option) => option.label}
          options={optionsWithEmptyValue}
          defaultValue={defaultValue}
          filterOptions={filterOptions}
          onOpen={() => (open ? open.set(true) : undefined)}
          onClose={() => (open ? open.set(false) : undefined)}
          openOnFocus={true}
          aria-errormessage={errorHelpText}
          selectOnFocus
          clearOnBlur
          handleHomeEndKeys
          fullWidth
          noOptionsText={noOptionsText}
          readOnly={(options.length === 0 || disabled) ?? undefined}
          slotProps={{
            paper: {
              sx: {
                "& .MuiAutocomplete-listbox": {
                  paddingTop: "0",
                  paddingBottom: "0",
                  "& .MuiAutocomplete-option": {
                    paddingLeft: "0px",
                    paddingRight: "0px",
                    backgroundColor: "white",
                    '&[aria-selected="true"]': {
                      backgroundColor: "#B6E6D8", // Selected background
                    },
                    "&.Mui-focusVisible": {
                      backgroundColor: "#E5EFF1", // Focused background
                      borderWidth: "2px",
                      borderColor: "#37261D",
                    },
                  },
                },
              },
            },
          }}
          componentsProps={{
            popper: {
              modifiers: [
                {
                  name: "offset",
                  options: { offset: [optionsPopperOffset, 4] },
                },
              ],
              style: {
                width: anchorRef?.current?.clientWidth || 200,
              },
            },
            paper: {
              sx: {
                wordBreak: "break-word",
              },
            },
          }}
          sx={{
            "& .MuiFilledInput-root": {
              background: "inherit !important",
              padding: "12px 10px !important",
            },
            "& .MuiFilledInput-root::before": {
              border: "none !important",
            },
            "& .MuiFilledInput-root::after": {
              border: "none !important",
            },
            "& .MuiInputBase-input": {
              padding: "0 !important",
              fontSize: "20px",
            },
          }}
          onChange={(_e, value) => {
            onChange(value);
          }}
          renderOption={(props, option, state) => {
            if (providedRenderOption) {
              return providedRenderOption(props, option, state);
            } else if (option.id && option.label) {
              return (
                <li
                  {...props}
                  key={state.index}
                  className={`text-xl text-ldir-brown-900 ${props.className} w-full flex flex-row items-center justify-between p-2`}
                >
                  {option.label}
                  {state.selected && <CheckIcon className="mr-2" />}
                </li>
              );
            }
            return <li key={state.index} />;
          }}
          renderInput={(params) => (
            <>
              {showId && (
                <Text variant={TextVariant.small} className="ml-2.5 -mb-3.5 mt-2">
                  {defaultValue?.id}
                </Text>
              )}

              <TextField
                {...params}
                aria-describedby={ariadescribedby}
                placeholder={placeholder}
                variant="filled"
                inputProps={{
                  ...params.inputProps,
                  type: inputMode,
                  readOnly: !!readOnlyInput || !!disableInput || disabled || options.length === 0,
                }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <div className="flex">
                      {params.InputProps.endAdornment}
                      {errorHelpText && <AlertIcon />}
                    </div>
                  ),
                }}
                sx={{
                  "& .MuiFilledInput-root .MuiAutocomplete-endAdornment": { right: 0 },
                  "& .MuiAutocomplete-endAdornment": { position: "relative", transform: "none" },
                }}
              />
            </>
          )}
          isOptionEqualToValue={(option, value) => option.id == value.id}
          autoHighlight={true}
          autoSelect={true}
        />
      </div>
      <div id={label + "helpText"}>
        {errorHelpText ? (
          <ErrorText isOnDarkBg={onDarkBg}>{errorHelpText}</ErrorText>
        ) : helpText ? (
          <Text>{helpText}</Text>
        ) : null}
      </div>
    </div>
  );
}

export type ComboboxOption<T = object> = {
  label: string;
  id: string;
  data?: T | null;
};
